import { useState } from 'react';
import type { BasicHealthData } from '../../types/health';
import { ACTIVITY_LEVELS } from '../../constants/health';

interface OnboardingStep3Props {
  onComplete: (data: Partial<BasicHealthData>) => void;
  onBack: () => void;
}

export default function OnboardingStep3({ onComplete, onBack }: OnboardingStep3Props) {
  const [activityLevel, setActivityLevel] = useState<BasicHealthData['activityLevel']>('moderate');
  const [smokingStatus, setSmokingStatus] = useState<BasicHealthData['smokingStatus']>('never');
  const [alcoholStatus, setAlcoholStatus] = useState<BasicHealthData['alcoholStatus']>('occasional');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete({ activityLevel, smokingStatus, alcoholStatus });
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
          <i className="ri-heart-pulse-line text-3xl text-teal-600"></i>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Lifestyle Habits</h2>
        <p className="text-lg text-gray-600">Help us understand your daily routine and habits</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-4">Activity Level</label>
          <div className="space-y-3">
            {ACTIVITY_LEVELS.map((level) => (
              <button
                key={level.value}
                type="button"
                onClick={() => setActivityLevel(level.value as any)}
                className={`w-full px-5 py-4 rounded-lg border-2 transition-all text-left ${
                  activityLevel === level.value
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className={`font-semibold ${activityLevel === level.value ? 'text-teal-700' : 'text-gray-900'}`}>
                      {level.label}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">{level.description}</div>
                  </div>
                  {activityLevel === level.value && (
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-check-line text-xl text-teal-600"></i>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-4">Smoking Status</label>
          <div className="grid grid-cols-3 gap-3">
            {[
              { value: 'never', label: 'Never' },
              { value: 'former', label: 'Former' },
              { value: 'current', label: 'Current' }
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setSmokingStatus(option.value as any)}
                className={`px-4 py-3 rounded-lg border-2 transition-all whitespace-nowrap ${
                  smokingStatus === option.value
                    ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-4">Alcohol Consumption</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { value: 'none', label: 'None' },
              { value: 'occasional', label: 'Occasional' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'heavy', label: 'Heavy' }
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setAlcoholStatus(option.value as any)}
                className={`px-4 py-3 rounded-lg border-2 transition-all whitespace-nowrap ${
                  alcoholStatus === option.value
                    ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-between pt-6">
          <button
            type="button"
            onClick={onBack}
            className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-gray-400 transition-colors whitespace-nowrap"
          >
            Back
          </button>
          <button
            type="submit"
            className="px-8 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
